import React, { useMemo, useState } from "react";
import { CrewPilotModuleShell } from "../../CrewPilotModuleShell";
import {
  buildPayrollReportModel,
  exportPayrollReportPdf,
} from "../../../helpers/payrollReport";
import { PayrollReportDocument } from "./PayrollReportDocument";
import { PayrollReportPrintDocument } from "./PayrollReportPrintDocument";
import {
  ReportPreviewChrome,
  ReportPreviewFrame,
  REPORT_PREVIEW_HELP_TEXT,
} from "../../report/ReportPreviewChrome";
import "./PayrollReportPreviewWide.css";
import { useIsDesktopViewport } from "../../../hooks/useIsDesktopViewport";
import { PAGE_LAYOUT } from "../../../layout/pageLayout";
import "../payrollDesktop.css";
import { useOrganization } from "../../../context/OrganizationContext";
import { resolveOrgBranding } from "../../../helpers/resolveOrgBranding";

const PREVIEW_LAYOUT = {
  MOBILE: "mobile",
  WIDE: "wide",
};

function PreviewLayoutToggle({ value, onChange }) {
  return (
    <fieldset className="payroll-preview-layout-toggle">
      <legend className="payroll-preview-layout-toggle__legend">Preview Layout</legend>
      <div className="payroll-preview-layout-toggle__options">
        <label className="payroll-preview-layout-toggle__option">
          <input
            type="radio"
            name="payroll-preview-layout"
            value={PREVIEW_LAYOUT.MOBILE}
            checked={value === PREVIEW_LAYOUT.MOBILE}
            onChange={() => onChange(PREVIEW_LAYOUT.MOBILE)}
          />
          <span>Mobile</span>
        </label>
        <label className="payroll-preview-layout-toggle__option">
          <input
            type="radio"
            name="payroll-preview-layout"
            value={PREVIEW_LAYOUT.WIDE}
            checked={value === PREVIEW_LAYOUT.WIDE}
            onChange={() => onChange(PREVIEW_LAYOUT.WIDE)}
          />
          <span>Wide</span>
        </label>
      </div>
    </fieldset>
  );
}

export function PayrollReportPreviewScreen({
  rows,
  meta,
  options,
  onBack,
  onEditOptions,
}) {
  const isDesktop = useIsDesktopViewport();
  const [previewLayout, setPreviewLayout] = useState(PREVIEW_LAYOUT.WIDE);
  const [pdfExporting, setPdfExporting] = useState(false);
  const { settings } = useOrganization();
  const branding = useMemo(() => resolveOrgBranding(settings), [settings]);

  const reportModel = useMemo(
    () =>
      buildPayrollReportModel(
        rows,
        { ...meta, orgSettings: meta?.orgSettings ?? settings },
        options,
      ),
    [rows, meta, options, settings],
  );

  const subtitle =
    reportModel?.header?.employeeName ||
    meta?.employeeInfo?.employee_name ||
    meta?.employeeName ||
    (meta?.startDate && meta?.endDate
      ? `${meta.startDate} – ${meta.endDate}`
      : "");

  const isWideLayout = previewLayout === PREVIEW_LAYOUT.WIDE;

  async function handleExportPdf() {
    try {
      setPdfExporting(true);
      await exportPayrollReportPdf(reportModel, { branding });
    } catch (error) {
      console.error("Payroll PDF export failed:", error);
      window.alert("Could not export the PDF. Please try again.");
    } finally {
      setPdfExporting(false);
    }
  }

  if (isDesktop) {
    return (
      <CrewPilotModuleShell
        title="Payroll Preview"
        subtitle={subtitle}
        onBack={onBack}
        desktopBackLabel="Report Options"
        layout={PAGE_LAYOUT.COMMAND}
        compact
        headerActions={
          <div className="cdh-actions">
            <div className="pay-seg" role="radiogroup" aria-label="Preview layout">
              <label>
                <input type="radio" name="payroll-preview-layout" checked={!isWideLayout} onChange={() => setPreviewLayout(PREVIEW_LAYOUT.MOBILE)} />
                Mobile
              </label>
              <label>
                <input type="radio" name="payroll-preview-layout" checked={isWideLayout} onChange={() => setPreviewLayout(PREVIEW_LAYOUT.WIDE)} />
                Wide
              </label>
            </div>
            {onEditOptions ? (
              <button type="button" className="dt-btn" onClick={onEditOptions}>Edit Options</button>
            ) : null}
            <button type="button" className="dt-btn dt-btn--primary" onClick={handleExportPdf} disabled={pdfExporting}>
              {pdfExporting ? "Exporting PDF…" : "Export PDF"}
            </button>
          </div>
        }
      >
        {isWideLayout ? (
          <div className="payroll-report-wide-preview-scroll report-preview-frame">
            <div className="payroll-report-wide-preview-frame">
              <PayrollReportPrintDocument reportModel={reportModel} />
            </div>
          </div>
        ) : (
          <ReportPreviewFrame>
            <PayrollReportDocument reportModel={reportModel} />
          </ReportPreviewFrame>
        )}
      </CrewPilotModuleShell>
    );
  }

  return (
    <CrewPilotModuleShell
      title="Payroll Preview"
      subtitle={subtitle}
      sectionLabel="Payroll Report"
      onBack={onBack}
    >
      <PreviewLayoutToggle value={previewLayout} onChange={setPreviewLayout} />

      {isWideLayout ? (
        <div className="payroll-report-wide-preview-scroll report-preview-frame">
          <div className="payroll-report-wide-preview-frame">
            <PayrollReportPrintDocument reportModel={reportModel} />
          </div>
        </div>
      ) : (
        <ReportPreviewFrame>
          <PayrollReportDocument reportModel={reportModel} />
        </ReportPreviewFrame>
      )}

      <ReportPreviewChrome
        helpText={
          isWideLayout
            ? "Wide layout shows a desktop-style report for screenshots. Switch to Mobile for the compact review view. Export PDF saves or shares a PDF from the report data."
            : `${REPORT_PREVIEW_HELP_TEXT} Switch to Wide for a desktop-style layout you can screenshot.`
        }
      >
        {onEditOptions && (
          <button type="button" className="nx-btn" onClick={onEditOptions}>
            Edit Options
          </button>
        )}
        <button
          type="button"
          className="nx-btn nx-btn-gold"
          onClick={handleExportPdf}
          disabled={pdfExporting}
        >
          {pdfExporting ? "Exporting PDF…" : "Export PDF"}
        </button>
      </ReportPreviewChrome>
    </CrewPilotModuleShell>
  );
}
